import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, ArrowUpRight, GitBranch, Wrench, Sparkles } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { SERVICES } from "@/data/services";

const ServiceDetail = () => {
  const { id } = useParams();
  const index = SERVICES.findIndex((s) => s.id === id);
  if (index === -1) return <Navigate to="/services" replace />;

  const service = SERVICES[index];
  const prev = SERVICES[(index - 1 + SERVICES.length) % SERVICES.length];
  const next = SERVICES[(index + 1) % SERVICES.length];
  const Icon = service.icon;

  return (
    <div data-testid="service-detail-page" className="pt-28 sm:pt-36 pb-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <Link
            to="/services"
            data-testid="detail-back-link"
            className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-gray-400 hover:text-lime transition-colors"
          >
            <ArrowLeft size={14} /> All technologies
          </Link>
        </Reveal>

        {/* HERO */}
        <div className="mt-8 relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-lime/10 via-carbon to-obsidian p-8 sm:p-12">
          <Icon className="absolute -right-8 -bottom-8 text-white/[0.05]" size={220} />
          <Reveal>
            <span className="inline-block font-mono text-[10px] uppercase tracking-[0.2em] text-lime border border-lime/30 rounded-full px-4 py-1.5">
              {service.category}
            </span>
            <h1 className="mt-6 font-display font-extrabold tracking-tight text-3xl sm:text-5xl max-w-3xl relative">
              {service.title}
            </h1>
            <p className="mt-4 max-w-2xl text-gray-300 text-base sm:text-lg leading-relaxed relative">
              {service.tagline} Engineered as medium to large capacity plants — from concept to
              creation, installation, commissioning and operation.
            </p>
          </Reveal>
        </div>

        {/* PROCESS FLOW */}
        <section className="mt-16">
          <Reveal>
            <Eyebrow>
              <GitBranch size={13} className="inline" /> Process flow
            </Eyebrow>
            <h2 className="mt-4 font-display font-bold tracking-tight text-2xl sm:text-3xl">
              How the plant runs, end to end
            </h2>
          </Reveal>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {service.flow.map((step, i) => (
              <Reveal key={step} delay={i * 0.07}>
                <div
                  data-testid={`detail-flow-step-${i + 1}`}
                  className="group relative h-full rounded-2xl border border-white/10 bg-carbon p-5 hover:border-lime/50 transition-colors duration-300"
                >
                  <p className="font-mono text-xs text-lime">STEP 0{i + 1}</p>
                  <p className="mt-3 font-semibold text-white text-sm leading-snug">{step}</p>
                  {i < service.flow.length - 1 && (
                    <ArrowRight className="hidden lg:block absolute -right-3.5 top-1/2 -translate-y-1/2 text-lime/50 z-10" size={16} />
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        {/* CONFIGURATION */}
        <section className="mt-16">
          <Reveal>
            <Eyebrow>
              <Wrench size={13} className="inline" /> Typical configuration
            </Eyebrow>
            <h2 className="mt-4 font-display font-bold tracking-tight text-2xl sm:text-3xl">
              What a typical plant includes
            </h2>
          </Reveal>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {service.config.map((item, i) => (
              <Reveal key={item} delay={i * 0.05}>
                <div
                  data-testid={`detail-config-${i + 1}`}
                  className="rounded-xl border border-white/10 bg-steel px-5 py-4 text-sm text-gray-200 hover:border-lime/40 hover:text-white transition-colors duration-300"
                >
                  {item}
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        {/* HIGHLIGHTS */}
        <section className="mt-16 grid lg:grid-cols-2 gap-10 items-start">
          <Reveal>
            <Eyebrow>
              <Sparkles size={13} className="inline" /> Scope highlights
            </Eyebrow>
            <ul className="mt-6 space-y-3">
              {service.points.map((p) => (
                <li key={p} className="flex gap-3 text-gray-300 leading-relaxed">
                  <span className="mt-2.5 h-1.5 w-1.5 rounded-full bg-lime shrink-0" />
                  {p}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="rounded-2xl border border-lime/30 bg-gradient-to-br from-lime/10 to-emerald/5 p-8">
              <h3 className="font-display font-bold text-xl text-white">Discuss this technology</h3>
              <p className="mt-3 text-sm text-gray-300 leading-relaxed">
                Tell us your feedstock, site and product goals — we will size a medium to large
                capacity configuration and take it from concept to commissioning.
              </p>
              <Link
                to={`/contact?service=${service.id}`}
                data-testid="detail-enquire-button"
                className="mt-6 inline-flex items-center gap-2 bg-lime text-obsidian font-semibold px-6 py-3 rounded-full hover:bg-lime-bright hover:shadow-[0_0_28px_rgba(132,204,22,0.4)] transition-all duration-300"
              >
                Enquire about {service.title} <ArrowUpRight size={16} />
              </Link>
            </div>
          </Reveal>
        </section>

        {/* PREV / NEXT */}
        <nav className="mt-20 pt-8 border-t border-white/10 grid sm:grid-cols-2 gap-4" aria-label="More technologies">
          <Link
            to={`/services/${prev.id}`}
            data-testid="detail-prev-link"
            className="group rounded-2xl border border-white/10 bg-carbon p-6 hover:border-lime/40 transition-colors"
          >
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gray-500 flex items-center gap-2">
              <ArrowLeft size={12} /> Previous
            </p>
            <p className="mt-2 font-display font-bold text-lg text-white group-hover:text-lime transition-colors">{prev.title}</p>
          </Link>
          <Link
            to={`/services/${next.id}`}
            data-testid="detail-next-link"
            className="group rounded-2xl border border-white/10 bg-carbon p-6 hover:border-lime/40 transition-colors text-right"
          >
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gray-500 flex items-center gap-2 justify-end">
              Next <ArrowRight size={12} />
            </p>
            <p className="mt-2 font-display font-bold text-lg text-white group-hover:text-lime transition-colors">{next.title}</p>
          </Link>
        </nav>
      </div>
    </div>
  );
};

export default ServiceDetail;
